/**
 * Phase 8 — 📹 LiveKit video tile.
 *
 * Renders a single LiveKit participant's camera track in a square tile,
 * falling back to their avatar (with a red 📷✕ chip) when no video is
 * being published. Designed to be a drop-in replacement for the inner
 * <img> avatar inside the existing Beam Box speaker grid.
 *
 * Props:
 *   participant      LiveKit Participant (local or remote) — optional
 *   fallbackUser     { user_id, name, profile_photo } — used when no
 *                    LiveKit participant is available (the room hasn't
 *                    connected yet, or the user is offline)
 *   isHost           amber border if true
 *   isPinned         shows pinned glow + larger size hint
 *   isMuted          mutes the audio output for this tile (host control)
 *   className        extra container classes
 */
import { useEffect, useRef, useState } from "react";
import { Track } from "livekit-client";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const _findVideoPub = (p) => {
  if (!p) return null;
  for (const pub of p.videoTrackPublications.values()) {
    // Prefer the camera track; ignore screen-share for the grid (out of
    // scope for v1).
    if (pub.source === Track.Source.Camera && pub.track && pub.isSubscribed) {
      return pub;
    }
  }
  return null;
};

const _findAudioPub = (p) => {
  if (!p) return null;
  for (const pub of p.audioTrackPublications.values()) {
    if (pub.source === Track.Source.Microphone && pub.track && pub.isSubscribed) {
      return pub;
    }
  }
  return null;
};

export const LiveKitVideoTile = ({
  participant = null,
  fallbackUser = null,
  isHost = false,
  isPinned = false,
  isMuted = false,
  className = "",
}) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(false);
  const [trackTick, setTrackTick] = useState(0);

  // Re-render when the participant's tracks change.
  useEffect(() => {
    if (!participant) return;
    const bump = () => setTrackTick((t) => t + 1);
    participant.on("trackSubscribed", bump);
    participant.on("trackUnsubscribed", bump);
    participant.on("trackPublished", bump);
    participant.on("trackUnpublished", bump);
    participant.on("trackMuted", bump);
    participant.on("trackUnmuted", bump);
    return () => {
      participant.off("trackSubscribed", bump);
      participant.off("trackUnsubscribed", bump);
      participant.off("trackPublished", bump);
      participant.off("trackUnpublished", bump);
      participant.off("trackMuted", bump);
      participant.off("trackUnmuted", bump);
    };
  }, [participant]);

  // Attach video — Phase 21.1g+ also re-runs on every trackTick so a
  // reconnect-driven trackSubscribed always rebinds the video element.
  // Extract dep primitives so ESLint can statically verify them.
  const _videoPubForDeps = _findVideoPub(participant);
  const videoTrackSid = _videoPubForDeps?.trackSid;
  const videoIsMuted = _videoPubForDeps?.isMuted;
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const pub = _findVideoPub(participant);
    if (pub && pub.track && !pub.isMuted) {
      try { pub.track.attach(el); setHasVideo(true); } catch (_e) {}
      return () => { try { pub.track.detach(el); } catch (_e) {} setHasVideo(false); };
    }
    setHasVideo(false);
  }, [participant, trackTick, videoTrackSid, videoIsMuted]);

  // Attach audio — Phase 21.1g+ also re-runs on trackTick so audio gets
  // rebound after a reconnect / track republish (no more "I see them but
  // can't hear them").
  const _audioPubForDeps = _findAudioPub(participant);
  const audioTrackSid = _audioPubForDeps?.trackSid;
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (participant?.isLocal) return;       // never play your own mic
    if (isMuted) { el.muted = true; return; }
    const pub = _findAudioPub(participant);
    if (pub && pub.track) {
      try { pub.track.attach(el); el.muted = false; } catch (_e) {}
      return () => { try { pub.track.detach(el); } catch (_e) {} };
    }
  }, [participant, trackTick, audioTrackSid, isMuted]);

  const name = participant?.name || fallbackUser?.name || "Listener";
  const avatarSrc = getProfilePhotoUrl(
    fallbackUser || { profile_photo: undefined, picture: undefined }
  );

  return (
    <div
      className={`relative rounded-xl overflow-hidden bg-black/60 border ${
        isPinned ? "border-amber-400/60 shadow-lg shadow-amber-500/20" :
        isHost   ? "border-amber-400/40" : "border-white/10"
      } ${className}`}
      data-testid={`livekit-tile-${participant?.identity || fallbackUser?.user_id || "anon"}`}
    >
      <div className="aspect-square w-full relative">
        {/* Video element — kept mounted even when no video, so attach/detach
            doesn't fight React. Hidden via CSS when no track. */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={participant?.isLocal === true}
          className={`w-full h-full object-cover ${hasVideo ? "" : "hidden"}`}
          data-testid={`livekit-video-${participant?.identity || "noid"}`}
        />
        {!hasVideo && (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-500/15 to-purple-500/15">
            <img
              src={avatarSrc}
              alt={name}
              className="w-1/2 h-1/2 rounded-full object-cover border-2 border-white/20"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop";
              }}
            />
          </div>
        )}
        {/* Audio output — never visible. */}
        <audio ref={audioRef} autoPlay playsInline className="hidden" />

        {/* Overlays */}
        {isHost && (
          <span className="absolute top-1 right-1 text-[10px] bg-amber-500 text-black rounded-full px-1.5 py-0.5 font-bold">
            🎙 HOST
          </span>
        )}
        {isPinned && (
          <span className="absolute top-1 left-1 text-[10px] bg-amber-500 text-black rounded-full px-1.5 py-0.5 font-bold">
            📌 PIN
          </span>
        )}
        {isMuted && (
          <span className="absolute bottom-1 right-1 text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5">
            🔇
          </span>
        )}
        {/* Name plate */}
        <span className="absolute bottom-1 left-1 right-9 text-[11px] bg-black/55 backdrop-blur-sm text-white rounded px-1.5 py-0.5 font-semibold truncate">
          {name}
        </span>
      </div>
    </div>
  );
};

export default LiveKitVideoTile;
